import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
import { BlogPost } from '../../core/config/site-config.interface';

@Component({
  selector: 'app-blog-list',
  standalone: true,
  imports: [RouterLink, ScrollAnimateDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Category + Tag Filters -->
    <section class="py-5 bg-white border-b border-surface-100 sticky top-[68px] z-30">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <!-- Categories -->
        <div class="flex flex-wrap gap-2 mb-3">
          @for (cat of page.categories; track cat) {
            <button (click)="setCategory(cat)"
                    class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    [class.bg-brand-600]="activeCategory() === cat"
                    [class.text-white]="activeCategory() === cat"
                    [class.bg-surface-100]="activeCategory() !== cat"
                    [class.text-surface-500]="activeCategory() !== cat"
                    [class.hover:bg-surface-200]="activeCategory() !== cat">
              {{ cat }}
            </button>
          }
        </div>
        <!-- Tags -->
        <div class="flex flex-wrap gap-1.5">
          @for (tag of page.tags; track tag) {
            <button (click)="toggleTag(tag)"
                    class="px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 border"
                    [class.bg-brand-50]="activeTags().has(tag)"
                    [class.text-brand-700]="activeTags().has(tag)"
                    [class.border-brand-200]="activeTags().has(tag)"
                    [class.bg-transparent]="!activeTags().has(tag)"
                    [class.text-surface-400]="!activeTags().has(tag)"
                    [class.border-surface-200]="!activeTags().has(tag)"
                    [class.hover:border-surface-300]="!activeTags().has(tag)"
                    [class.hover:text-surface-600]="!activeTags().has(tag)">
              #{{ tag }}
            </button>
          }
          @if (activeTags().size > 0) {
            <button (click)="clearTags()"
                    class="px-3 py-1 rounded-full text-xs font-medium text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 transition-colors">
              Clear tags
            </button>
          }
        </div>
      </div>
    </section>

    <!-- Featured Post -->
    @if (featuredPost() && activeCategory() === 'All' && activeTags().size === 0) {
      <section class="py-12 md:py-16 bg-white">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <a [routerLink]="'/blog/' + featuredPost()!.slug"
             class="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div class="aspect-[16/10] rounded-2xl overflow-hidden relative">
              <img [src]="featuredPost()!.image" [alt]="featuredPost()!.title"
                   class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div class="absolute top-4 left-4">
                <span class="px-3 py-1.5 bg-brand-600 text-white text-[0.65rem] font-bold uppercase tracking-wider rounded-md">
                  Featured
                </span>
              </div>
            </div>
            <div>
              <div class="flex items-center gap-3 mb-4">
                <span class="px-2.5 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-md">{{ featuredPost()!.category }}</span>
                <span class="text-xs text-surface-400">{{ featuredPost()!.date }}</span>
                <span class="text-xs text-surface-400">&middot; {{ featuredPost()!.readTime }}</span>
              </div>
              <h2 class="font-heading text-2xl md:text-3xl font-bold text-surface-900 mb-4 group-hover:text-brand-600 transition-colors tracking-tight">
                {{ featuredPost()!.title }}
              </h2>
              <p class="text-surface-500 leading-relaxed mb-6">{{ featuredPost()!.excerpt }}</p>
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold">
                  {{ featuredPost()!.author.initials }}
                </div>
                <div>
                  <div class="text-sm font-semibold text-surface-900">{{ featuredPost()!.author.name }}</div>
                  <div class="text-xs text-surface-400">{{ featuredPost()!.author.role }}</div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>
    }

    <!-- Posts Grid -->
    <section class="py-12 md:py-16 bg-surface-50">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        @if (filteredPosts().length > 0) {
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            @for (post of filteredPosts(); track post.slug; let i = $index) {
              <article appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="((i % 3) + 1) * 80"
                       class="group bg-white rounded-2xl overflow-hidden border border-surface-100 transition-all duration-300 hover:shadow-xl hover:shadow-surface-900/[0.06] hover:-translate-y-1">
                <a [routerLink]="'/blog/' + post.slug" class="block">
                  <!-- Image -->
                  <div class="aspect-[16/10] overflow-hidden relative">
                    <img [src]="post.image" [alt]="post.title"
                         class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <div class="absolute top-3 left-3">
                      <span class="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-surface-700 text-[0.6rem] font-bold uppercase tracking-wider rounded-md shadow-sm">
                        {{ post.category }}
                      </span>
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="p-6">
                    <div class="flex items-center gap-2 mb-3">
                      <time class="text-xs text-surface-400">{{ post.date }}</time>
                      <span class="text-surface-300">&middot;</span>
                      <span class="text-xs text-surface-400">{{ post.readTime }}</span>
                    </div>
                    <h3 class="font-heading text-base font-bold text-surface-900 mb-2 group-hover:text-brand-600 transition-colors line-clamp-2 leading-snug">
                      {{ post.title }}
                    </h3>
                    <p class="text-sm text-surface-500 leading-relaxed line-clamp-2 mb-4">{{ post.excerpt }}</p>

                    <!-- Tags -->
                    <div class="flex flex-wrap gap-1.5 mb-4">
                      @for (tag of post.tags.slice(0, 3); track tag) {
                        <span class="px-2 py-0.5 bg-surface-50 text-[0.65rem] font-medium text-surface-400 rounded">
                          #{{ tag }}
                        </span>
                      }
                    </div>

                    <!-- Author -->
                    <div class="flex items-center gap-2.5 pt-4 border-t border-surface-100">
                      <div class="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-[0.6rem] font-bold">
                        {{ post.author.initials }}
                      </div>
                      <span class="text-xs font-medium text-surface-600">{{ post.author.name }}</span>
                    </div>
                  </div>
                </a>
              </article>
            }
          </div>
        } @else {
          <div class="text-center py-20">
            <div class="w-16 h-16 rounded-full bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-5">
              <svg aria-hidden="true" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <h3 class="font-heading text-lg font-bold text-surface-900 mb-2">No posts found</h3>
            <p class="text-sm text-surface-500 mb-6">Try changing the category or clearing the tag filters.</p>
            <button (click)="resetFilters()" class="text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
              Reset all filters
            </button>
          </div>
        }
      </div>
    </section>

    <!-- Newsletter CTA -->
    <section class="py-16 md:py-20 bg-brand-600 relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500"></div>
      <div class="mx-auto max-w-3xl px-6 lg:px-8 text-center relative z-10">
        <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Stay in the Loop</h2>
        <p class="text-brand-100 mb-8 max-w-lg mx-auto">Get the latest tips, trends, and project inspiration delivered to your inbox.</p>
        <div class="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
          <input type="email" placeholder="Your email address"
                 class="flex-1 w-full px-5 py-3 bg-white/10 border border-white/20 text-white placeholder-white/50 rounded-lg text-sm focus:outline-none focus:border-white/40 focus:bg-white/15 transition-colors" />
          <button class="w-full sm:w-auto px-7 py-3 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors whitespace-nowrap">
            Subscribe
          </button>
        </div>
      </div>
    </section>
  `,
})
export class BlogListComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.blog;

  activeCategory = signal('All');
  activeTags = signal<Set<string>>(new Set());

  featuredPost = computed(() => {
    return this.page.posts.find(p => p.featured) || null;
  });

  filteredPosts = computed(() => {
    const cat = this.activeCategory();
    const tags = this.activeTags();
    const featured = this.featuredPost();

    let posts = this.page.posts;

    // Exclude featured post from grid when showing unfiltered
    if (cat === 'All' && tags.size === 0 && featured) {
      posts = posts.filter(p => p !== featured);
    }

    if (cat !== 'All') {
      posts = posts.filter(p => p.category === cat);
    }

    if (tags.size > 0) {
      posts = posts.filter(p => p.tags.some(t => tags.has(t)));
    }

    return posts;
  });

  ngOnInit(): void {
    this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blog' },
    ]));
  }

  setCategory(cat: string): void {
    this.activeCategory.set(cat);
  }

  toggleTag(tag: string): void {
    this.activeTags.update(tags => {
      const next = new Set(tags);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }

  clearTags(): void {
    this.activeTags.set(new Set());
  }

  resetFilters(): void {
    this.activeCategory.set('All');
    this.activeTags.set(new Set());
  }
}
